import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Cart drawer terms/privacy consent checkbox (snippets/cart-summary.liquid,
 * rendered with `show_terms_checkbox: true` only from snippets/cart-drawer.liquid)
 * — keeps the checkout button disabled until the box is checked.
 *
 * Event delegation on `document`, not a listener bound to the checkbox
 * itself: the cart drawer's contents are re-fetched and morphed in on every
 * cart change (add/remove/quantity update), which replaces the checkbox
 * element with a fresh (unchecked) one from the server-rendered HTML each
 * time. A listener attached directly to the old element would simply stop
 * existing after the first cart update; delegation on `document` keeps
 * working no matter how many times the element underneath it gets swapped.
 * `#checkout` is safe to look up globally: the drawer only ever renders
 * when the cart page itself isn't showing (see the guard in
 * cart-drawer.liquid), so the two never coexist on the same page.
 */
document.addEventListener('change', (event) => {
  const checkbox = /** @type {HTMLElement} */ (event.target);
  if (!(checkbox instanceof HTMLInputElement) || !checkbox.classList.contains('cart-terms-consent__checkbox')) {
    return;
  }

  const checkoutButton = document.getElementById('checkout');
  if (checkoutButton instanceof HTMLButtonElement) {
    checkoutButton.disabled = !checkbox.checked;
  }
});

/**
 * Drag-vs-tap guard for every `.resource-card__media-scroller` on the page
 * (snippets/resource-card.liquid's mobile image gallery — used by
 * predictive search, mega menus, header drawers, related products, etc.).
 * `.resource-card__media` is a real link to the product page, so a swipe
 * that ends in a pointerup over it would otherwise also navigate.
 *
 * This used to live as an inline `<script>` inside resource-card.liquid
 * itself, guarded by a `window.__veyaResourceCardGalleryBound` flag so it
 * only bound once no matter how many cards rendered. That works fine when
 * a card is part of the initial server-rendered HTML — but predictive
 * search's dropdown results are fetched live and spliced into the DOM via
 * `assets/predictive-search.js`'s `morph()` call, and browsers never
 * execute a `<script>` tag that arrives that way (only scripts the HTML
 * parser itself encounters during a real document load run automatically).
 * On any page where no resource-card happened to be server-rendered
 * up front, that inline script never ran even once — so opening
 * predictive search and swiping a result card would tap through to the
 * product page instead of revealing the second photo, with no error to
 * point at why.
 *
 * Living here instead — a real `<script type="module">` loaded
 * unconditionally by the header, which is part of every single page's
 * initial HTML — guarantees this binds exactly once before the user can
 * ever open predictive search, regardless of what else is on the page.
 * Event delegation on `document` then covers every card added later by
 * morph/fetch for free, the same as it always did.
 */
let resourceCardStartScrollLeft = 0;

document.addEventListener(
  'pointerdown',
  (event) => {
    const scroller = /** @type {HTMLElement} */ (event.target).closest?.('.resource-card__media-scroller');
    if (scroller) resourceCardStartScrollLeft = scroller.scrollLeft;
  },
  { passive: true }
);

document.addEventListener(
  'click',
  (event) => {
    const scroller = /** @type {HTMLElement} */ (event.target).closest?.('.resource-card__media-scroller');
    if (scroller && Math.abs(scroller.scrollLeft - resourceCardStartScrollLeft) > 2) {
      event.preventDefault();
    }
  },
  true
);

/**
 * Drag-vs-tap guard for every `.veya-product-card__media-scroller` on the
 * page (snippets/veya-product-card.liquid's mobile image gallery — the
 * shared product card used by veya-featured-products, veya-main-collection
 * and veya-search-results). `.veya-product-card__media` is a real link to
 * the product page, so a swipe that ends in a pointerup over it would
 * otherwise also navigate.
 *
 * One delegated listener here, on `document`, serves every card on the
 * page (and any added later, e.g. "load more") for free — see the
 * `.resource-card__media-scroller` block above for why delegation from the
 * header (rather than a listener attached per-card) is required here too.
 */
let productCardStartScrollLeft = 0;

document.addEventListener(
  'pointerdown',
  (event) => {
    const scroller = /** @type {HTMLElement} */ (event.target).closest?.('.veya-product-card__media-scroller');
    if (scroller) productCardStartScrollLeft = scroller.scrollLeft;
  },
  { passive: true }
);

document.addEventListener(
  'click',
  (event) => {
    const scroller = /** @type {HTMLElement} */ (event.target).closest?.('.veya-product-card__media-scroller');
    if (scroller && Math.abs(scroller.scrollLeft - productCardStartScrollLeft) > 2) {
      event.preventDefault();
    }
  },
  true
);

/**
 * Root header component.
 *
 * - Tracks scroll position via an IntersectionObserver on a sentinel element
 *   to toggle the sticky "stuck" visual state (background swap, shadow).
 * - Keeps the mobile hamburger trigger's `aria-expanded` in sync with the
 *   shared `<theme-drawer>` it controls, listening for the drawer's own
 *   (bubbling) open/close events rather than duplicating its logic.
 *
 * @typedef {object} HeaderRefs
 * @property {HTMLButtonElement} drawerTrigger - The hamburger button that opens the mobile drawer.
 *
 * @extends {Component<HeaderRefs>}
 */
class VeyaHeaderComponent extends Component {
  requiredRefs = ['drawerTrigger'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    // The sentinel is a preceding sibling (not a descendant): it must scroll
    // away in normal flow while this element is pinned via `position: sticky`,
    // so its visibility marks the exact moment the header becomes "stuck".
    const sentinel = this.previousElementSibling;

    if (this.hasAttribute('sticky') && sentinel) {
      this.#observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.classList.toggle('veya-header--stuck', !entry.isIntersecting);
      });
      this.#observer.observe(sentinel);
    }

    this.addEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.addEventListener('theme-drawer:close', this.#onDrawerClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.removeEventListener('theme-drawer:close', this.#onDrawerClose);
  }

  #onDrawerOpen = () => {
    this.refs.drawerTrigger.setAttribute('aria-expanded', 'true');
  };

  #onDrawerClose = () => {
    this.refs.drawerTrigger.setAttribute('aria-expanded', 'false');
  };
}

if (!customElements.get('veya-header-component')) {
  customElements.define('veya-header-component', VeyaHeaderComponent);
}

/**
 * Mobile menu navigation-stack controller.
 *
 * Renders a native-app-style panel stack inside the shared `<theme-drawer>`:
 * pushing into a submenu slides a new panel in from the right while the
 * current one slides out to the left; going back reverses it. Only the
 * active panel is interactive — every other panel is `inert` and
 * `aria-hidden`. The stack resets to the root panel only after the drawer's
 * own close animation has fully finished, so no reset is ever visible.
 *
 * @typedef {object} MobileMenuRefs
 * @property {HTMLElement} panels - The container holding every panel.
 *
 * @extends {Component<MobileMenuRefs>}
 */
class VeyaMobileMenu extends Component {
  requiredRefs = ['panels'];

  /** @type {string[]} */
  #stack = ['root'];

  /** @type {{x: number, y: number} | null} */
  #touchStart = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
    document.addEventListener('theme-drawer:close', this.#onDrawerClose);
    this.addEventListener('touchstart', this.#onRowTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onRowTouchEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    document.removeEventListener('theme-drawer:close', this.#onDrawerClose);
    this.removeEventListener('touchstart', this.#onRowTouchStart);
    this.removeEventListener('touchend', this.#onRowTouchEnd);
  }

  /**
   * Any tappable row in this drawer — a leaf link (real `<a href>`), a
   * submenu-opening row, or a "back" row (both plain `<button>`s) —
   * sometimes needs a second tap on mobile to actually register: the first
   * tap's synthesized `click` event can be silently dropped by iOS Safari
   * whenever ANY DOM mutation happens anywhere in the document during that
   * same touch's own touchstart→touchend→click cascade — a real,
   * documented browser bug, not something specific to this menu. Earlier
   * fixes here each targeted one specific mutation believed to be the
   * cause (the drawer's own opening-class cleanup, then a `:hover` CSS
   * quirk), and an even earlier version of this exact `touchend` bypass
   * only covered leaf links — each tested on the merchant's actual phone
   * and confirmed to still leave SOME row inconsistent (first leaf links,
   * then confirmed the identical symptom on submenu-opening rows too).
   *
   * Rather than keep hunting for every possible mutation, or patching one
   * row type at a time, this bypasses the unreliable `click` synthesis for
   * every row type at once: `touchend` itself is never dropped this way,
   * so acting directly from it — and calling `preventDefault()` to cancel
   * the browser's own (unreliable) follow-up click — makes the first tap
   * reliable regardless of whatever else on the page happens to mutate at
   * the same moment. Guarded by a touchstart→touchend distance check so a
   * genuine scroll/swipe inside the drawer's own scrollable panel never
   * misfires as a tap — same drag-vs-tap shape as the
   * `.resource-card__media-scroller`/`.veya-product-card__media-scroller`
   * guards elsewhere in this file, adapted from tracking `scrollLeft` to
   * tracking raw touch distance (there's no analogous scroll-position
   * signal here to compare against). Desktop (mouse, no touch events) is
   * completely unaffected — normal click handling (native `<a href>`
   * navigation, the framework's own `on:click` dispatch for the buttons)
   * keeps working exactly as before there.
   *
   * @param {TouchEvent} event
   */
  #onRowTouchStart = (event) => {
    const touch = event.touches[0];
    this.#touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /** @param {TouchEvent} event */
  #onRowTouchEnd = (event) => {
    const row = /** @type {Element} */ (event.target).closest?.(
      'a.veya-mobile-drawer__row[href], button.veya-mobile-drawer__row, button.veya-mobile-drawer__back'
    );
    const start = this.#touchStart;
    this.#touchStart = null;
    if (!row || !start) return;

    const touch = event.changedTouches[0];
    if (touch) {
      const distance = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
      // A real scroll/swipe, not a tap — let it behave as a normal scroll
      // gesture, untouched.
      if (distance > 10) return;
    }

    event.preventDefault();

    // A real, visible press moment — see the CSS class comment on
    // `.is-pressed` — needed specifically because acting on `touchend`
    // this way means nothing here waits around for the browser's own
    // default tap-highlight/`:active` styling to render before moving on;
    // without this, a leaf link (which navigates away next) had no
    // visible confirmation the tap registered at all before the page
    // changed, unlike a submenu row, which already gets one for free from
    // its own panel-slide animation having time to render first.
    row.classList.add('is-pressed');

    if (row instanceof HTMLAnchorElement) {
      // Delayed just long enough for `.is-pressed` to actually be seen
      // before the page navigates away and this element is gone.
      window.setTimeout(() => {
        window.location.href = row.href;
      }, 120);
      return;
    }

    window.setTimeout(() => row.classList.remove('is-pressed'), 150);

    if (row.classList.contains('veya-mobile-drawer__back')) {
      this.goBack();
      return;
    }

    // Mirrors `openPanel`'s own declarative `on:click` invocation — see
    // `data-panel-target` on this same button in sections/veya-header.liquid
    // (rendered with the exact same value as its `on:click` attribute's
    // `panel=` query param, kept in sync by hand since both come from the
    // same Liquid variable at that call site). Passes `row` itself as
    // `event.target` — `openPanel` only ever reads `.target` off its
    // `event` argument (see its own comment for why: the framework's real
    // delegated listener lives on `document`, so `.currentTarget` there is
    // never the actual trigger) — matching the resolved trigger BUTTON the
    // framework's own dispatch would pass, not whatever inner `<span>` the
    // touch happened to land on.
    const panelTarget = /** @type {HTMLElement} */ (row).dataset.panelTarget;
    if (panelTarget) {
      this.openPanel({ panel: panelTarget }, /** @type {Event} */ ({ target: row }));
    }
  };

  /** @param {Event} event */
  #ownsEvent(event) {
    return event.target === this.closest('theme-drawer');
  }

  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    if (!this.#ownsEvent(event)) return;
    this.#playStagger(this.#findPanel('root'));
  };

  /** @param {Event} event */
  #onDrawerClose = (event) => {
    if (!this.#ownsEvent(event)) return;

    const dialogEl = this.closest('dialog');
    if (dialogEl) {
      onAnimationEnd(dialogEl, () => this.#resetToRoot(), { subtree: false });
    } else {
      this.#resetToRoot();
    }
  };

  /**
   * Resolves a short panel key (e.g. `'root'`) or a full `data-panel-id`
   * value to its panel element.
   *
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #findPanel(key) {
    const id = key === 'root' ? this.dataset.rootPanel : key;
    if (!id) return null;
    return /** @type {HTMLElement | null} */ (this.refs.panels.querySelector(`[data-panel-id="${id}"]`));
  }

  #resetToRoot() {
    const root = this.#findPanel('root');

    for (const panel of this.refs.panels.querySelectorAll('[data-panel-id]')) {
      if (panel === root || !(panel instanceof HTMLElement)) continue;
      panel.style.transform = 'translateX(-100%)';
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
    }

    if (root) {
      root.style.transform = '';
      root.setAttribute('aria-hidden', 'false');
      root.removeAttribute('inert');
    }

    for (const trigger of this.refs.panels.querySelectorAll('[aria-expanded="true"]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.#stack = ['root'];
  }

  /**
   * Opens a submenu panel. Called declaratively via
   * `on:click="veya-mobile-menu-component/openPanel?panel=..."`.
   *
   * @param {{panel: string}} data
   * @param {Event} event
   */
  openPanel(data, event) {
    const targetKey = data.panel;
    const from = this.#findPanel(this.#stack[this.#stack.length - 1] ?? 'root');
    const to = this.#findPanel(targetKey);
    if (!to || to === from) return;

    // `event.target`, not `event.currentTarget`: the framework's real
    // `addEventListener` lives on `document` (event delegation), so
    // `currentTarget` here is always `document`, never the clicked trigger.
    const trigger = event.target;
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'true');

    this.#transition(from, to, 'forward');
    this.#stack.push(targetKey);
    this.#playStagger(to);
  }

  /**
   * Returns to the parent panel. Called declaratively via
   * `on:click="veya-mobile-menu-component/goBack"`.
   */
  goBack() {
    if (this.#stack.length < 2) return;

    const fromKey = this.#stack.pop();
    if (!fromKey) return;

    const from = this.#findPanel(fromKey);
    const to = this.#findPanel(this.#stack[this.#stack.length - 1] ?? 'root');

    const trigger = /** @type {HTMLElement | null} */ (
      this.refs.panels.querySelector(`[on\\:click*="${fromKey}"]`)
    );
    trigger?.setAttribute('aria-expanded', 'false');

    this.#transition(from, to, 'back');
  }

  /**
   * @param {HTMLElement | null} from
   * @param {HTMLElement | null} to
   * @param {'forward' | 'back'} direction
   */
  #transition(from, to, direction) {
    if (!from || !to) return;

    // RTL drill-down: pushing a submenu enters from the left and exits to the
    // right; going back reverses it.
    to.removeAttribute('inert');
    to.setAttribute('aria-hidden', 'false');
    to.style.transform = direction === 'forward' ? 'translateX(-100%)' : 'translateX(100%)';

    // Force layout so the starting transform is committed before animating.
    void to.offsetWidth;

    requestAnimationFrame(() => {
      to.style.transform = 'translateX(0)';
      from.style.transform = direction === 'forward' ? 'translateX(100%)' : 'translateX(-100%)';
    });

    onAnimationEnd(from, () => {
      from.setAttribute('aria-hidden', 'true');
      from.setAttribute('inert', '');
    });
  }

  /** @param {HTMLElement | null} panel */
  #playStagger(panel) {
    if (!panel) return;

    for (const item of panel.querySelectorAll('.veya-mobile-drawer__item')) {
      item.classList.remove('is-visible');
      if (item instanceof HTMLElement) void item.offsetWidth;
      item.classList.add('is-visible');
    }
  }
}

if (!customElements.get('veya-mobile-menu-component')) {
  customElements.define('veya-mobile-menu-component', VeyaMobileMenu);
}
